import React, { useEffect, useState } from 'react'
import { Checkbox, Col, ColProps, Form, Input, Radio, Row, Select, Upload, UploadFile } from 'antd'
import type { RcFile, UploadProps } from 'antd/lib/upload'
import { useFormik } from 'formik'
import { NumericFormat } from 'react-number-format'
import { Add, Image } from 'iconsax-react'
import axios from 'axios'
import dayjs from 'dayjs'
import Card from 'components/card/Card'
import CardHeaderForm from 'components/card/CardHeaderForm'
import RequiredMark from 'components/form/RequiredMark'
import Label from 'components/form/Label'
import Button from 'components/button/Button'
import RichTextEditor from 'components/form/RichTextEditor'
import { validationSchemaFormJobVacancy } from 'helpers/validation'
import {
  classNameCheckBox,
  classNameInput,
  classNameRadioGroup,
} from 'helpers/formik/classNameForm'
import { isImage, isSizeMaxAllowed, isSizeMinAllowed } from 'helpers/imageUpload'
import {
  categoryJobVacanciesEnum,
  collectionMethodEnum,
  facilitiesEnum,
  soiltTypeEnum,
  wageUnitJobVacancyEnum,
} from './hooks/jobVacancyEnum'
import { useJobVacancyForm } from './hooks/useJobVacancyForm'
import {
  AdminsJobBoardsVacanciesCreateJobVacancyRequest,
  AdminsJobBoardsVacanciesUpdateJobVacancyRequest,
  Configuration,
  MediaApi,
} from 'generated/api'
import { axiosDefaultConfig } from 'constants/axios'

const classNameForm = 'flex flex-col gap-3 xl:gap-9'
const layout = 'horizontal'
const preserve = false
const colon = false
const labelAlign = 'left'
const labelCol: ColProps = { prefixCls: 'xl:w-3/12' }
const wrapperCol: ColProps = { prefixCls: 'xl:w-9/12' }
const style = { maxWidth: '100%' }

const axiosConfig = new Configuration(axiosDefaultConfig)
const mediaApi = new MediaApi(axiosConfig)

export default function JobVacancyForm() {
  const useJobVacancyFormHooks = useJobVacancyForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])

  const formik = useFormik({
    initialValues: useJobVacancyFormHooks.jobVacancyForm,
    validationSchema: validationSchemaFormJobVacancy,
    enableReinitialize: true,
    validateOnBlur: true,
    onSubmit: async values => {
      if (useJobVacancyFormHooks.jobId) {
        await useJobVacancyFormHooks.updateJobVacancy(
          values as AdminsJobBoardsVacanciesUpdateJobVacancyRequest,
        )
      } else {
        await useJobVacancyFormHooks.createJobVacancy(
          values as AdminsJobBoardsVacanciesCreateJobVacancyRequest,
        )
      }
    },
  })

  useEffect(() => {
    if (formik.values.images?.length) {
      const updatedFileList = formik.values.images.map((item, index) => {
        return {
          uid: index,
          name: item,
          status: 'done',
          url: item,
        }
      })
      setFileList(updatedFileList as unknown as UploadFile[])
    }
  }, [formik.values.images])

  const { values, errors, touched, setFieldValue, setFieldTouched, setFieldError } = formik

  const imageProps: UploadProps = {
    multiple: false,
    fileList: fileList,
    iconRender: () => {
      return <Image size={24} />
    },
    onRemove: file => {
      const updatedFiles = fileList.filter(item => item.uid !== file.uid)
      setFileList(updatedFiles as UploadFile[])
      formik.setFieldValue(
        'images',
        updatedFiles.map(item => item.url),
      )
    },
    beforeUpload: async (file: RcFile) => {
      const isImageFile = isImage(file)
      const isSizeMinAllowedFile = isSizeMinAllowed(file)
      const isSizeMaxAllowedFile = isSizeMaxAllowed(file)

      if (!isImageFile) {
        await setFieldTouched('images', true)
        setFieldError(
          'images',
          `${file.name} tidak valid. Hanya menerima file dengan format PNG, JPG, JPEG`,
        )
        return false
      }

      if (!isSizeMinAllowedFile) {
        await setFieldTouched('images', true)
        setFieldError('images', `${file.name} minimal ukuran lebih dari 1KB`)
        return false
      }

      if (!isSizeMaxAllowedFile) {
        await setFieldTouched('images', true)
        setFieldError('images', `${file.name} melebihi ukuran maksimal 2MB`)
        return false
      }

      return (isImageFile && isSizeMaxAllowedFile && isSizeMinAllowedFile) || Upload.LIST_IGNORE
    },
    customRequest: async ({ file }) => {
      const uploadFile = file as RcFile
      const updatedFileList = [
        ...fileList,
        { uid: uploadFile.uid, name: uploadFile.name, status: 'uploading', percent: 0 },
      ]
      try {
        setFileList(updatedFileList as UploadFile[])
        const date = dayjs().toDate().valueOf()
        const responsePresign = await mediaApi.mediaPresignedUrl({
          operationType: 'Ecommerce',
          mimeType: uploadFile.type,
          originalFileName: `job-board/vacancy/${date}`,
          fileSize: uploadFile.size,
        })
        await axios.put(responsePresign.data.presignedUrl, file, {
          headers: {
            'Content-Type': uploadFile.type,
          },
          onUploadProgress: progressEvent => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              )
              const updatedFileListProgress = updatedFileList.map(item => {
                if (item.uid === uploadFile.uid) {
                  return { ...item, percent: percentCompleted }
                }
                return item
              })
              setFileList(updatedFileListProgress as UploadFile[])
            }
          },
        })

        const updatedFileListDone = updatedFileList.map(item => {
          if (item.uid === uploadFile.uid) {
            return {
              uid: uploadFile.uid,
              name: uploadFile.name,
              status: 'done',
              url: responsePresign.data.publicUrl,
            }
          }
          return item
        })
        const updatedImageUrlItems = formik.values.images?.concat(responsePresign.data.publicUrl)
        setFileList(updatedFileListDone as UploadFile[])
        await formik.setFieldValue('images', updatedImageUrlItems)
      } catch (err) {
        const updatedFileListError = updatedFileList.map(item => {
          if (item.uid === uploadFile.uid) {
            return { ...item, status: 'error' }
          }
          return item
        })
        setFileList(updatedFileListError as UploadFile[])
      }
    },
  }

  const fixedFacilities = [
    facilitiesEnum.TEMPATTINGGAL,
    facilitiesEnum.PERALATANKERJA,
    facilitiesEnum.UANGSAKU,
    facilitiesEnum.MAKANPAGI,
    facilitiesEnum.MAKANSIANG,
    facilitiesEnum.MAKANMALAM,
  ]

  const valueRadioFacility = (findFacility: facilitiesEnum) =>
    values.facilities.find(facility => facility === findFacility) || ''

  const handleOnClickFacilitiesRadio = (value: string, facility: facilitiesEnum) => {
    value
      ? handleChangeFacilitiesRadio(value, 'add')
      : handleChangeFacilitiesRadio(facility, 'remove')
  }

  const handleChangeFacilitiesRadio = (value: string, type: 'add' | 'remove') => {
    const facilities = values.facilities

    if (type === 'add') {
      if (!facilities.includes(value)) {
        setFieldValue('facilities', [...facilities, value])
      }
    } else if (type === 'remove') {
      setFieldValue(
        'facilities',
        facilities.filter(facility => facility !== value),
      )
    }
  }

  const handleChangesFacilitiesCheckBox = (checkedValue: string) => {
    if (values.facilities.includes(checkedValue)) {
      const facilities = values.facilities.filter(facility => facility !== checkedValue)
      setFieldValue('facilities', facilities)
    } else {
      setFieldValue('facilities', [...values.facilities, checkedValue])
    }
  }

  const handleChangeFacilitiesTags = (additionalFacilities: string[]) => {
    const newFacilities = values.facilities.filter(item =>
      fixedFacilities.includes(item as facilitiesEnum),
    )
    const updatedFacilities = newFacilities.concat(additionalFacilities)
    const removedDuplicateFacilities = Array.from(new Set(updatedFacilities))
    setFieldValue('facilities', removedDuplicateFacilities)
  }

  const handleChangeTerrainSoilType = (value: string) => {
    const soilType = values.terrain.soilType.length ? values.terrain.soilType.split(',') : []
    if (soilType.includes(value)) {
      const newSoilType = soilType.filter(item => item !== value)
      setFieldValue('terrain.soilType', newSoilType.join())
    } else {
      setFieldValue('terrain.soilType', [...soilType, value].join())
    }
  }

  const loading = useJobVacancyFormHooks.fetchStatus === 'loading'
  const validateStatusJobName = errors.jobName && touched.jobName ? 'error' : undefined
  const helpJobName = errors.jobName && touched.jobName ? errors.jobName : null
  const validateStatusWageMinimum =
    errors.wage?.minimum && touched.wage?.minimum ? 'error' : undefined
  const helpWageMinimum =
    errors.wage?.minimum && touched.wage?.minimum ? errors.wage?.minimum : null
  const validateStatusWageMaximum =
    errors.wage?.maximum && touched.wage?.maximum ? 'error' : undefined
  const helpWageMaximum =
    errors.wage?.maximum && touched.wage?.maximum ? errors.wage?.maximum : null
  const validateStatusQuota = errors.quota && touched.quota ? 'error' : undefined
  const helpQuota = errors.quota && touched.quota ? errors.quota : null
  const validateStatusAreaId = errors.areaId && touched.areaId ? 'error' : undefined
  const helpAreaId = errors.areaId && touched.areaId ? errors.areaId : null

  const radioCategoryJob = [
    {
      disabled: false,
      value: categoryJobVacanciesEnum.HARVESTER,
      label: 'Pemanen',
    },
    {
      disabled: true,
      value: categoryJobVacanciesEnum.UPKEEPER,
      label: 'Perawat',
    },
    {
      disabled: true,
      value: categoryJobVacanciesEnum.SHIPPER,
      label: 'Pengiriman',
    },
  ]

  const radioWageUnit = [
    wageUnitJobVacancyEnum.TON,
    wageUnitJobVacancyEnum.KG,
    wageUnitJobVacancyEnum.HA,
    wageUnitJobVacancyEnum.BORONGAN,
    wageUnitJobVacancyEnum.JANJANG,
  ]

  const radioTempatTinggal = [
    { label: 'Tidak Disediakan', value: '' },
    { label: 'Disediakan', value: facilitiesEnum.TEMPATTINGGAL },
  ]

  const radioPeralatanKerja = [
    { label: 'Tidak Disediakan', value: '' },
    { label: 'Disediakan', value: facilitiesEnum.PERALATANKERJA },
  ]

  const radioUangSaku = [
    { label: 'Tidak Disediakan', value: '' },
    { label: 'Disediakan', value: facilitiesEnum.UANGSAKU },
  ]

  const checkBoxMakan = [
    facilitiesEnum.MAKANPAGI,
    facilitiesEnum.MAKANSIANG,
    facilitiesEnum.MAKANMALAM,
  ]

  const optionsAdditionalFacilities = [
    {
      value: facilitiesEnum.TINGGALSENDIRI,
      label: facilitiesEnum.TINGGALSENDIRI,
    },
    {
      value: facilitiesEnum.DISEDIAKANKENDARAAN,
      label: facilitiesEnum.DISEDIAKANKENDARAAN,
    },
    {
      value: facilitiesEnum.TUNAI,
      label: facilitiesEnum.TUNAI,
    },
    {
      value: facilitiesEnum.TRANSFERBANK,
      label: facilitiesEnum.TRANSFERBANK,
    },
  ]

  const checkBoxSoilType = [
    soiltTypeEnum.GAMBUT,
    soiltTypeEnum.MINERAL,
    soiltTypeEnum.SULFATMASAM,
    soiltTypeEnum.BERPASIR,
  ]

  const radioCollectionMethod = [collectionMethodEnum.DIPIKUL, collectionMethodEnum.KERETASORONG]

  return (
    <div className="content-wrapper">
      {useJobVacancyFormHooks.toastContainer}
      <Card className="bg-transparent p-0" loading={loading}>
        <div className="flex flex-col gap-4">
          <Card className="rounded-xl py-6 px-0" dataTestId="card_basicInfo_form">
            <CardHeaderForm title="1. Informasi Dasar" />
            <Card.Body className="mt-0 px-6">
              <Form
                className={classNameForm}
                layout={layout}
                preserve={preserve}
                colon={colon}
                requiredMark={RequiredMark}
                labelAlign={labelAlign}
                labelWrap
                labelCol={labelCol}
                wrapperCol={wrapperCol}
                style={style}
              >
                <Form.Item
                  name="jobName"
                  valuePropName="jobname"
                  label={<Label text="Judul Pekerjaan" />}
                  className="mb-0"
                  required
                  validateStatus={validateStatusJobName}
                  help={helpJobName}
                >
                  <Input
                    data-testid="input_jobName"
                    placeholder="Masukkan Judul Pekerjaan"
                    className={classNameInput('jobName', formik)}
                    onChange={({ target: { value } }) => setFieldValue('jobName', value)}
                    value={values.jobName}
                  />
                </Form.Item>
                <Form.Item
                  valuePropName="categorycode"
                  label={<Label text="Kategori Pekerjaan" />}
                  className="mb-0"
                  required
                >
                  <Radio.Group
                    id="categoryCode"
                    className="flex flex-col xl:flex-row gap-6"
                    onChange={({ target: { value } }) => setFieldValue('categoryCode', value)}
                    value={values.categoryCode}
                  >
                    {radioCategoryJob.map(item => (
                      <Radio
                        key={item.value}
                        disabled={item.disabled}
                        className={`${classNameRadioGroup(values.categoryCode, item.value)} ${item.disabled ? 'opacity-20' : ''}`.trim()}
                        value={item.value}
                      >
                        {item.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  name="quota"
                  valuePropName="quota"
                  label={<Label text="Jumlah Pekerja" />}
                  className="mb-0"
                  required
                  validateStatus={validateStatusQuota}
                  help={helpQuota}
                >
                  <NumericFormat
                    allowNegative={false}
                    customInput={Input}
                    decimalScale={0}
                    decimalSeparator=","
                    placeholder="Masukkan Jumlah Pekerja"
                    thousandSeparator="."
                    className={classNameInput('quota', formik)}
                    value={values.quota ? values.quota : ''}
                    onValueChange={val => setFieldValue('quota', val.floatValue ?? '')}
                  />
                </Form.Item>
                <Form.Item
                  valuePropName="wage"
                  label={<Label text="Estimasi Penawaran Harga" />}
                  className="mb-0"
                  required
                >
                  <Row gutter={[24, 24]}>
                    <Col span={24} lg={{ span: 12 }}>
                      <Form.Item
                        valuePropName="wage.minimum"
                        validateStatus={validateStatusWageMinimum}
                        help={helpWageMinimum}
                      >
                        <NumericFormat
                          allowNegative={false}
                          prefix="Rp "
                          customInput={Input}
                          decimalScale={0}
                          decimalSeparator=","
                          placeholder="Rp Harga Mulai Dari"
                          thousandSeparator="."
                          className={classNameInput('wage.minimum', formik)}
                          value={values.wage.minimum ? values.wage.minimum : ''}
                          onValueChange={val => setFieldValue('wage.minimum', val.floatValue ?? '')}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24} lg={{ span: 12 }}>
                      <Form.Item
                        valuePropName="wage.maximum"
                        validateStatus={validateStatusWageMaximum}
                        help={helpWageMaximum}
                      >
                        <NumericFormat
                          allowNegative={false}
                          prefix="Rp "
                          customInput={Input}
                          decimalScale={0}
                          decimalSeparator=","
                          placeholder="Rp Harga Sampai Dengan"
                          thousandSeparator="."
                          className={classNameInput('wage.maximum', formik)}
                          value={values.wage.maximum ? values.wage.maximum : ''}
                          onValueChange={val => setFieldValue('wage.maximum', val.floatValue ?? '')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
                <Form.Item
                  valuePropName="wage.unit"
                  label={<Label text="Satuan Harga" />}
                  className="mb-0"
                  required
                >
                  <Radio.Group
                    id="wage.unit"
                    className="flex flex-col xl:flex-row gap-6"
                    onChange={({ target: { value } }) => setFieldValue('wage.unit', value)}
                    value={values.wage.unit}
                  >
                    {radioWageUnit.map(item => (
                      <Radio
                        key={item}
                        className={classNameRadioGroup(values.wage.unit, item)}
                        value={item}
                      >
                        {item}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </Form>
            </Card.Body>
          </Card>
          <Card className="rounded-xl py-6 px-0" dataTestId="card_facilities_form">
            <CardHeaderForm title="2. Fasilitas dari Pemberi Kerja" />
            <Card.Body className="mt-0 px-6">
              <Form
                className={classNameForm}
                layout={layout}
                preserve={preserve}
                colon={colon}
                requiredMark={RequiredMark}
                labelAlign={labelAlign}
                labelWrap
                labelCol={labelCol}
                wrapperCol={wrapperCol}
                style={style}
              >
                <Form.Item
                  valuePropName="facilities"
                  label={<Label text="Tempat Tinggal" />}
                  className="mb-0"
                >
                  <Radio.Group
                    id="facilities"
                    className="flex flex-col xl:flex-row gap-6"
                    value={valueRadioFacility(facilitiesEnum.TEMPATTINGGAL)}
                  >
                    {radioTempatTinggal.map(item => (
                      <Radio
                        key={item.value}
                        className={classNameRadioGroup(
                          valueRadioFacility(facilitiesEnum.TEMPATTINGGAL),
                          item.value,
                        )}
                        value={item.value}
                        onClick={() =>
                          handleOnClickFacilitiesRadio(item.value, facilitiesEnum.TEMPATTINGGAL)
                        }
                      >
                        {item.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  valuePropName="facilities"
                  label={<Label text="Konsumsi" />}
                  className="mb-0"
                >
                  <Checkbox.Group value={values.facilities} className="w-full">
                    <Row gutter={[24, 24]}>
                      {checkBoxMakan.map(item => (
                        <Col span={24} lg={{ span: 8 }} key={item}>
                          <Checkbox
                            value={item}
                            className={classNameCheckBox(
                              values.facilities.find(facility => facility === item) || '',
                              item,
                            )}
                            onChange={({ target: { value } }) =>
                              handleChangesFacilitiesCheckBox(value)
                            }
                          >
                            {item}
                          </Checkbox>
                        </Col>
                      ))}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  valuePropName="facilities"
                  label={<Label text="Peralatan Kerja" />}
                  className="mb-0"
                >
                  <Radio.Group
                    id="facilities"
                    className="flex flex-col xl:flex-row gap-6"
                    value={valueRadioFacility(facilitiesEnum.PERALATANKERJA)}
                  >
                    {radioPeralatanKerja.map(item => (
                      <Radio
                        key={item.value}
                        className={classNameRadioGroup(
                          valueRadioFacility(facilitiesEnum.PERALATANKERJA),
                          item.value,
                        )}
                        value={item.value}
                        onClick={() =>
                          handleOnClickFacilitiesRadio(item.value, facilitiesEnum.PERALATANKERJA)
                        }
                      >
                        {item.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  valuePropName="facilities"
                  label={<Label text="Uang Saku" />}
                  className="mb-0"
                >
                  <Radio.Group
                    id="facilities"
                    className="flex flex-col xl:flex-row gap-6"
                    value={valueRadioFacility(facilitiesEnum.UANGSAKU)}
                  >
                    {radioUangSaku.map(item => (
                      <Radio
                        key={item.value}
                        className={classNameRadioGroup(
                          valueRadioFacility(facilitiesEnum.UANGSAKU),
                          item.value,
                        )}
                        value={item.value}
                        onClick={() =>
                          handleOnClickFacilitiesRadio(item.value, facilitiesEnum.UANGSAKU)
                        }
                      >
                        {item.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  valuePropName="facilities"
                  label={<Label text="Informasi Tambahan" />}
                  className="mb-0"
                >
                  <Select
                    placeholder="Pilih Fasilitas Tambahan"
                    mode="tags"
                    className="selector h-full"
                    listHeight={800}
                    style={{ width: '100%', height: 'auto' }}
                    onChange={value => handleChangeFacilitiesTags(value)}
                    tokenSeparators={[',']}
                    options={optionsAdditionalFacilities}
                    value={values.facilities.filter(
                      item => !fixedFacilities.includes(item as facilitiesEnum),
                    )}
                  />
                </Form.Item>
              </Form>
            </Card.Body>
          </Card>
          <Card className="rounded-xl py-6 px-0" dataTestId="card_terrain_form">
            <CardHeaderForm title="3. Informasi Lahan" />
            <Card.Body className="mt-0 px-6">
              <Form
                className={classNameForm}
                layout={layout}
                preserve={preserve}
                colon={colon}
                requiredMark={RequiredMark}
                labelAlign={labelAlign}
                labelWrap
                labelCol={labelCol}
                wrapperCol={wrapperCol}
                style={style}
              >
                <Form.Item
                  valuePropName="images"
                  label={<Label text="Foto Lahan" />}
                  className="font-bold mb-1"
                  validateStatus={errors.images && touched.images ? 'error' : ''}
                  help={errors.images && touched.images ? errors.images : null}
                >
                  <Upload listType="picture-card" className="font-medium" {...imageProps}>
                    <button style={{ border: 0, background: 'none' }} type="button">
                      <Add size={14} />
                      <div className="mt-2 text-[#000] opacity-45">Upload</div>
                    </button>
                  </Upload>
                </Form.Item>

                <Form.Item
                  name="areaid"
                  valuePropName="areaid"
                  label={<Label text="Lokasi Lahan" />}
                  className="mb-0"
                  required
                  validateStatus={validateStatusAreaId}
                  help={helpAreaId}
                >
                  <Select
                    id="areaid"
                    showSearch
                    className="selector h-full"
                    placeholder="Cari Lokasi"
                    status={errors.areaId && touched.areaId ? 'error' : ''}
                    virtual={false}
                    filterOption={false}
                    onSearch={value => useJobVacancyFormHooks.fetchDistrictsByName(value)}
                    onPopupScroll={useJobVacancyFormHooks.fetchDistrictsByPage}
                    onChange={value => {
                      const district = useJobVacancyFormHooks.districts.find(
                        district => district.id === value,
                      )
                      setFieldValue('areaId', district?.id)
                      useJobVacancyFormHooks.setAreaName(district?.name as string)
                    }}
                    value={
                      useJobVacancyFormHooks.areaName
                        ? useJobVacancyFormHooks.areaName
                        : values.areaId
                    }
                    labelInValue={false}
                  >
                    {useJobVacancyFormHooks.districts.map(district => {
                      return (
                        <Select.Option key={district.id} value={district.id}>
                          {district.name}
                        </Select.Option>
                      )
                    })}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="terrain.condition"
                  valuePropName="terrain.condition"
                  label={<Label text="Kondisi Lahan" />}
                  className="mb-0"
                >
                  <Input
                    data-testid="input_terrainCondition"
                    placeholder="Masukkan Kondisi Lahan"
                    className={classNameInput('terrain.condition', formik)}
                    onChange={({ target: { value } }) => setFieldValue('terrain.condition', value)}
                    value={values.terrain.condition}
                  />
                </Form.Item>
                <Form.Item
                  valuePropName="terrain.soilType"
                  label={<Label text="Jenis Tanah" />}
                  className="mb-0"
                  required
                  validateStatus={
                    errors.terrain?.soilType && touched.terrain?.soilType ? 'error' : ''
                  }
                  help={
                    errors.terrain?.soilType && touched.terrain?.soilType
                      ? errors.terrain?.soilType
                      : null
                  }
                >
                  <Checkbox.Group value={values.terrain.soilType.split(',')} className="w-full">
                    <Row gutter={[24, 24]}>
                      {checkBoxSoilType.map(item => (
                        <Col span={24} lg={{ span: 12 }} key={item}>
                          <Checkbox
                            value={item}
                            onChange={({ target: { value } }) => handleChangeTerrainSoilType(value)}
                            className={classNameCheckBox(
                              values.terrain.soilType.length
                                ? values.terrain.soilType.split(',').find(soil => soil === item) ||
                                    ''
                                : '',
                              item,
                            )}
                          >
                            {item}
                          </Checkbox>
                        </Col>
                      ))}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
                <Form.Item
                  valuePropName="terrain.minimumTreeHigh"
                  label={<Label text="Tinggi Pohon" />}
                  className="mb-0"
                  required
                >
                  <Radio.Group
                    id="terrain.minimumTreeHigh"
                    className="flex flex-col xl:flex-row gap-6"
                    value={values.terrain.minimumTreeHigh}
                  >
                    {[6, 10, 15, 20].map(item => (
                      <Radio
                        key={item}
                        className={classNameRadioGroup(values.terrain.minimumTreeHigh, item)}
                        value={item}
                        onClick={() => setFieldValue('terrain.minimumTreeHigh', item)}
                      >
                        {`< ${item} Meter`}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  valuePropName="ffb"
                  label={<Label text="Estimasi Jarak Langsir" />}
                  className="mb-0"
                >
                  <Row gutter={[24, 24]}>
                    <Col span={24} lg={{ span: 12 }}>
                      <NumericFormat
                        allowNegative={false}
                        customInput={Input}
                        decimalScale={0}
                        decimalSeparator=","
                        placeholder="Contoh: 100 meter"
                        thousandSeparator="."
                        className={classNameInput('ffb.minimumEstimation', formik)}
                        value={values.ffb.minimumEstimation ? values.ffb.minimumEstimation : ''}
                        onValueChange={val =>
                          setFieldValue('ffb.minimumEstimation', val.floatValue ?? '')
                        }
                      />
                    </Col>
                    <Col span={24} lg={{ span: 12 }}>
                      <NumericFormat
                        allowNegative={false}
                        customInput={Input}
                        decimalScale={0}
                        decimalSeparator=","
                        placeholder="Contoh: 100 meter"
                        thousandSeparator="."
                        className={classNameInput('ffb.maximumEstimation', formik)}
                        value={values.ffb.maximumEstimation ? values.ffb.maximumEstimation : ''}
                        onValueChange={val =>
                          setFieldValue('ffb.maximumEstimation', val.floatValue ?? '')
                        }
                      />
                    </Col>
                  </Row>
                </Form.Item>
                <Form.Item
                  valuePropName="ffb.collectionMethod"
                  label={<Label text="Melangsir Dengan" />}
                  className="mb-0"
                >
                  <Radio.Group
                    id="ffb.collectionMethod"
                    className="flex flex-col xl:flex-row gap-6"
                    value={values.ffb.collectionMethod}
                  >
                    {radioCollectionMethod.map(item => (
                      <Radio
                        key={item}
                        className={classNameRadioGroup(values.ffb.collectionMethod || '', item)}
                        value={item}
                        onClick={() => setFieldValue('ffb.collectionMethod', item)}
                      >
                        {item}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  valuePropName="ffb.additionalParameter"
                  label={<Label text="Informasi Tambahan" />}
                  className="mb-0"
                >
                  <Input
                    data-testid="input_ffb_additionalParameter"
                    placeholder="Masukkan Informasi Tambahan"
                    className={classNameInput('ffb.additionalParameter', formik)}
                    onChange={({ target: { value } }) =>
                      setFieldValue('ffb.additionalParameter', value)
                    }
                    value={values.ffb.additionalParameter}
                  />
                </Form.Item>
              </Form>
            </Card.Body>
          </Card>
          <Card className="rounded-xl py-6 px-0" dataTestId="card_description_form">
            <CardHeaderForm title="4. Detail Pekerjaan" />
            <Card.Body className="mt-0 px-6">
              <Form
                className={classNameForm}
                layout={layout}
                preserve={preserve}
                colon={colon}
                requiredMark={RequiredMark}
                labelAlign={labelAlign}
                labelWrap
                labelCol={labelCol}
                wrapperCol={wrapperCol}
                style={style}
              >
                <Form.Item
                  valuePropName="jobdescription"
                  label={<Label text="Deskripsi" />}
                  className="font-bold mb-1"
                >
                  <RichTextEditor
                    value={values.jobDescription || ''}
                    onChange={val => {
                      setFieldValue('jobDescription', val)
                    }}
                  />
                </Form.Item>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Card>
      <div className="mt-4 flex justify-end gap-4">
        <Button
          data-testid="btn_cancel"
          className="pt-3 rounded-xl pb-3 font-bold text-sm"
          label="Batalkan"
          variant="outline-primary"
          loading={loading}
          onClick={useJobVacancyFormHooks.navigateToJobVacancies}
          type="submit"
        />
        <Button
          data-testid="btn_accept"
          className="pt-3 rounded-xl pb-3 font-bold text-sm"
          label="Simpan"
          variant="solid-primary"
          loading={loading}
          onClick={formik.handleSubmit}
          type="submit"
        />
      </div>
    </div>
  )
}
